import { useState } from 'react';
import { Button } from './ui/Button';
import { BookingFields } from './BookingFields';
import { ScheduleBanner } from './ScheduleTile';
import { updateJob } from '../lib/jobsBackend';
import {
  bookingFields, bookingFromSchedule, bookingWindowIsBackwards, jobSchedule, sameBooking,
  type BookingForm,
} from '../lib/schedule';
import type { Job } from '../types/models';

/**
 * The job page's booking (PR-43, v7 design Bobby approved on 1 Nov 2026): a one-line
 * banner when a date is set; for the office, "When: Not set" with its caption when it
 * isn't, and a Change button that edits it in place. A driver sees the banner only.
 * Saves through updateJob as one unit; the page's live listener shows the result.
 */
export function JobBookingPanel({
  job,
  companyId,
  canBook,
}: {
  job: Job;
  companyId: string | null | undefined;
  canBook: boolean;
}) {
  const schedule = jobSchedule(job);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BookingForm>(() => bookingFromSchedule(schedule));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!schedule.start && !canBook) return null;

  const startEditing = () => {
    setDraft(bookingFromSchedule(schedule));
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!companyId || !job.id || !canBook || bookingWindowIsBackwards(draft)) return;
    if (sameBooking(draft, bookingFromSchedule(schedule))) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateJob({ companyId, jobId: job.id, updates: bookingFields(draft) });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The booking could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="basis-full rounded-[8px] border border-hair bg-elev p-5">
        <BookingFields value={draft} onChange={setDraft} idPrefix="job-booking" />
        {error && <p className="mt-3 text-[13px] text-st-cancelled">{error}</p>}
        <div className="mt-4 flex gap-2">
          <Button onClick={save} loading={saving} disabled={!canBook || bookingWindowIsBackwards(draft)}>Save booking</Button>
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-3">
      {schedule.start ? (
        <ScheduleBanner schedule={schedule} />
      ) : (
        <div className="text-[14px]">
          <span className="text-ink3">When </span>
          <span className="font-medium text-ink">Not set</span>
          <span className="ml-2 text-[13px] text-ink3">Not set shows as ASAP on Live Jobs.</span>
        </div>
      )}
      {canBook && (
        <Button variant="ghost" size="sm" onClick={startEditing}>
          {schedule.start ? 'Change' : 'Set date and time'}
        </Button>
      )}
    </div>
  );
}
